import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { PageEvent as PageEvent } from '@angular/material/paginator';
import { DatePipe } from '@angular/common';
import * as fromRoot from '../../../../../../../state/app.state';
import { WorkflowService } from '../../../../../../../shared/services/workflow.service';

@Component({
    selector: 'app-changes-wrapper',
    templateUrl: './changes-wrapper.component.html',
    styleUrls: ['./changes-wrapper.component.scss'],
    providers: [DatePipe],
    standalone: false
})
export class ChangesWrapperComponent implements OnInit, OnDestroy {
  reportId = '';
  public loading = 0;
  public initialized = false;
  public pageSize = 20;

  public lineage = [];
  public pagination = {
    pageIndex: 0,
    length: 0,
    pageSize: this.pageSize,
  };

  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private route: ActivatedRoute,
    private router: Router,
    private wfService: WorkflowService,
    private datePipe: DatePipe,
    public dialog: MatDialog,
  ) {
  }

  ngOnInit() {

    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        if (qp.reportId) {
          if (qp.reportId !== this.reportId) {
            this.reportId = qp.reportId;
            this.fetchLineage();
          }
        } else {
          this.reportId = '';
          this.initialized = false;
          this.lineage = [];
        }
      });

    // Subscribe to lineage data from store
    this.store.select((state: any) => state['sitenotes.projects']?.dailyReportLineage)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          this.lineage = data.result || [];
          this.pagination.length = data.meta?.totalRecords || 0;
          this.initialized = true;
          this.loading = 0;
        }
      });
  }

  /**
   * Format date for display
   * @param date The date to format
   * @returns Formatted date string
   */
  formatDate(date: string | Date): string {
    if (!date) {
      return '';
    }

    try {
      // Convert to Date object if it's a string
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return this.datePipe.transform(dateObj, 'MMM d, y, h:mm:ss a') || '';
    } catch (error) {
      console.error('Error formatting date:', error);
      return typeof date === 'string' ? date : '';
    }
  }

  /**
   * Fetches lineage of the daily report
   */
  fetchLineage() {
    if (!this.reportId) {
      return;
    }

    this.initialized = false;
    this.loading++;

    // Format the ex parameter as an array with both approval and withdrawal paths
    // Change this to be dynamic for existing workflow states
    const exParam = [
      `site-daily-report-approval/${this.reportId}`,
      `site-daily-report-withdrawal/${this.reportId}`,
    ];


    // Try direct service call first for debugging
    this.wfService.fetchLineage(exParam, this.pagination, { include: ['actor', 'state'] })
      .subscribe({
        next: (response) => {
          this.lineage = response.result || [];
          this.pagination.length = response.meta?.totalRecords || 0;
          this.initialized = true;
          this.loading = 0;
        },
        error: (err) => {
          console.error('API call error:', err);
          this.initialized = true;
          this.loading = 0;
        },
      });

    // Set a safety timeout to ensure loading state is reset if something goes wrong
    setTimeout(() => {
      if (this.loading > 0) {
        this.loading = 0;
        this.initialized = true;
      }
    }, 10000); // 10 second timeout
  }

  /**
   * callback on pagination change
   * @param event PageEvent
   */
  pageChanged(event: PageEvent) {
    this.pagination = {
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      length: event.length,
    };
    this.fetchLineage();
  }

  /**
   * Get comment text from input.formValues
   * Handles both string and object formats
   * @param input The input object from lineage
   * @returns The comment text or empty string
   */
  getCommentText(input: any): string {
    if (!input?.formValues) {
      return '';
    }

    // Handle string format (direct comment text)
    if (typeof input.formValues === 'string') {
      return input.formValues;
    }

    // Handle object format with comments property
    if (typeof input.formValues === 'object' && input.formValues.comments) {
      return input.formValues.comments;
    }

    return '';
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
